import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import GlobalSearch from "../components/GlobalSearch";
import HealthBanner from "../components/HealthBanner";
import {
  LayoutDashboard, Users, Handshake, Briefcase, FileText, Calendar,
  Bell, Settings, LogOut, Shield, ScrollText, Menu as MenuIcon, Archive, Database, Search,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clients", label: "Clienti", icon: Users, perm: "subjects.read", testid: "nav-clients" },
  { to: "/partners", label: "Partner", icon: Handshake, perm: "partners.read", testid: "nav-partners" },
  { to: "/bandi", label: "Bandi e strumenti", icon: ScrollText, perm: "bandi.read", testid: "nav-bandi" },
  { to: "/practices", label: "Pratiche", icon: Briefcase, perm: "practices.read", testid: "nav-practices" },
  { to: "/documents", label: "Documenti", icon: FileText, perm: "documents.read", testid: "nav-documents" },
  { to: "/calendar", label: "Agenda", icon: Calendar, perm: "calendar.read", testid: "nav-calendar" },
  { to: "/notifications", label: "Notifiche", icon: Bell, testid: "nav-notifications" },
];

const ADMIN_NAV = [
  { to: "/admin/users", label: "Utenti", icon: Shield, testid: "nav-users" },
  { to: "/admin/audit", label: "Registro attività", icon: ScrollText, testid: "nav-audit" },
  { to: "/admin/archived", label: "Archivio", icon: Archive, testid: "nav-archived" },
  { to: "/admin/gdpr", label: "GDPR", icon: Shield, testid: "nav-gdpr" },
  { to: "/admin/backup", label: "Backup", icon: Database, testid: "nav-backup" },
  { to: "/admin/settings", label: "Impostazioni", icon: Settings, testid: "nav-settings" },
];

const USER_NAV_EXTRA = [
  { to: "/profile/notifications", label: "Notifiche browser", icon: Bell, testid: "nav-push-settings" },
];

export default function AppLayout() {
  const { user, logout, has } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const doLogout = async () => { await logout(); nav("/login"); };
  const initials = ((user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")).toUpperCase() || "?";

  // Shortcut Ctrl/Cmd+K per la ricerca globale
  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  return (
    <div className="ma-app-shell">
      <aside className={`ma-sidebar ${open ? "open" : ""}`}>
        <div className="ma-brand">
          <div className="ma-logo-placeholder" data-testid="app-logo">MASI<span>ADVICE</span></div>
          <div className="ma-subbrand">CRM</div>
        </div>
        <nav className="ma-nav">
          {NAV.filter(i => !i.perm || has(i.perm)).map(i => (
            <NavLink key={i.to} to={i.to} end={i.to === "/"} data-testid={i.testid}
                     className={({isActive}) => `ma-nav-item ${isActive ? "active" : ""}`}
                     onClick={() => setOpen(false)}>
              <i.icon size={18} strokeWidth={2} /><span>{i.label}</span>
            </NavLink>
          ))}
          <div className="ma-nav-section">Profilo</div>
          {USER_NAV_EXTRA.map(i => (
            <NavLink key={i.to} to={i.to} data-testid={i.testid}
                     className={({isActive}) => `ma-nav-item ${isActive ? "active" : ""}`}
                     onClick={() => setOpen(false)}>
              <i.icon size={18} strokeWidth={2} /><span>{i.label}</span>
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <>
              <div className="ma-nav-section">Amministrazione</div>
              {ADMIN_NAV.map(i => (
                <NavLink key={i.to} to={i.to} data-testid={i.testid}
                         className={({isActive}) => `ma-nav-item ${isActive ? "active" : ""}`}
                         onClick={() => setOpen(false)}>
                  <i.icon size={18} strokeWidth={2} /><span>{i.label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>
      </aside>
      <div className="ma-main">
        <header className="ma-topbar">
          <button className="ma-hamburger" onClick={() => setOpen(!open)} data-testid="topbar-menu">
            <MenuIcon size={20} />
          </button>
          <button className="ma-btn-secondary" onClick={()=>setSearchOpen(true)} data-testid="topbar-search-btn"
                  style={{minWidth:280, justifyContent:"flex-start", gap:8, color:"var(--ma-text-muted)"}}>
            <Search size={14}/> Cerca ovunque...
            <span style={{marginLeft:"auto", fontSize:11, background:"var(--ma-surface-2)", padding:"2px 6px", borderRadius:4}}>Ctrl+K</span>
          </button>
          <div className="ma-topbar-spacer" />
          <div className="ma-user">
            <div className="ma-avatar" data-testid="user-avatar">{initials}</div>
            <div className="ma-user-info">
              <div className="ma-user-name">{user?.first_name} {user?.last_name}</div>
              <div className="ma-user-role">{user?.role === "admin" ? "Amministratore" : "Collaboratore"}</div>
            </div>
            <button className="ma-btn-icon" onClick={doLogout} title="Esci" data-testid="btn-logout">
              <LogOut size={18} />
            </button>
          </div>
        </header>
        <HealthBanner />
        <main className="ma-content"><Outlet /></main>
      </div>
      <GlobalSearch open={searchOpen} onClose={()=>setSearchOpen(false)}/>
    </div>
  );
}
